import type {
  NotificationOwnerDto,
  NotificationSeverity,
  ScheduleCreateBody,
  ScheduleListQuery,
  ScheduleOwnerDto,
  SchedulePatchBody,
  ScheduleRunsQuery,
  ScheduleRunStatus,
  ScheduleRunSummaryDto,
} from '@verybigthings/trackframe-shared/schemas';
// `truncateId` was moved to the shared formatter (lib/format). Import it for
// local use (safeRunSummary) and re-export so existing importers of this
// module (schedules/List, schedules/RunHistory) keep working.
import { truncateId } from '@/lib/format';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';
export type ScheduleEnabledFilter = 'all' | 'enabled' | 'paused';

export interface ScheduleListFilters {
  enabledFilter: ScheduleEnabledFilter;
  trackId?: string;
}

export interface ScheduleNotificationSummary {
  unreadCount: number;
  latestLabel: string;
  highestSeverity: NotificationSeverity | null;
  badgeVariant: BadgeVariant;
}

export interface ScheduleFormValues {
  trackId: string;
  scheduleExpr: string;
  timezone: string;
  prompt: string;
  toolAllowlistText: string;
  wholePlanPreapproved: boolean;
  enabled: boolean;
  /** COP-134 D3 / ATRU-03 — opt into the `scheduled_succeeded` notification. */
  notifyOnSuccess: boolean;
}

export type ScheduleValidationHints = Partial<Record<keyof ScheduleFormValues, string>>;

/**
 * The `Omit<…, 'metadata'>` is deliberate: the run metadata bag is untrusted
 * diagnostics and never reaches an admin component. COP-134 D1 / ATRU-04 adds
 * the ONE fact the bag used to hide as a typed DTO field instead — `sessionId`
 * flows through the Omit, and `sessionLabel` renders it in the same style as
 * `traceLabel` / `approvalLabel`.
 */
export type SafeScheduleRunSummary = Omit<ScheduleRunSummaryDto, 'metadata'> & {
  statusLabel: string;
  badgeVariant: BadgeVariant;
  scheduledForLabel: string;
  startedAtLabel: string;
  finishedAtLabel: string;
  durationLabel: string;
  retryLabel: string;
  errorLabel: string;
  traceLabel: string;
  approvalLabel: string;
  /** COP-134 D1 / ATRU-04 — truncated `sessionId`, or `—` when the run never opened a session. */
  sessionLabel: string;
};

const TOOL_NAME_RE = /^[A-Za-z0-9_.:-]+$/;
const FIVE_FIELD_CRON_RE = /^\S+\s+\S+\s+\S+\s+\S+\s+\S+$/;
const IANA_TIMEZONE_RE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)+$/;

const STATUS_LABELS: Record<ScheduleRunStatus, string> = {
  claimed: 'Claimed',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  denied: 'Denied',
  waiting_approval: 'Waiting for approval',
  skipped: 'Skipped',
  paused: 'Paused',
  waiting: 'Waiting',
};

const NOTIFICATION_SEVERITY_RANK: Record<NotificationSeverity, number> = {
  info: 1,
  warning: 2,
  error: 3,
};

export const WHOLE_PLAN_PREAPPROVAL_WARNING =
  'Whole-plan preapproval lets each scheduled run execute the allowed tools without per-run approval. Use it only with a narrow prompt and minimal tool allowlist.';

export function formatScheduleDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(
    date.getUTCHours(),
  )}:${pad2(date.getUTCMinutes())} UTC`;
}

export function formatRunDuration(
  run: Pick<ScheduleRunSummaryDto, 'startedAt' | 'finishedAt'>,
): string {
  const start = parseDate(run.startedAt);
  if (!start) return '—';
  const finish = parseDate(run.finishedAt);
  if (!finish) return 'In progress';

  const durationMs = finish.getTime() - start.getTime();
  if (durationMs < 0) return '—';
  if (durationMs < 1_000) return `${durationMs}ms`;

  const totalSeconds = Math.floor(durationMs / 1_000);
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  if (minutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

export function runStatusBadgeVariant(status: ScheduleRunStatus): BadgeVariant {
  switch (status) {
    case 'succeeded':
      return 'secondary';
    case 'failed':
    case 'denied':
      return 'destructive';
    case 'skipped':
    case 'paused':
      return 'outline';
    case 'claimed':
    case 'running':
    case 'waiting_approval':
    case 'waiting':
      return 'default';
  }
}

// COP-160 P82 — a schedule that is turned off reads "Off": "Paused" names a RUN state.
export function getScheduleStateLabel(enabled: boolean): { label: 'Enabled' | 'Off'; badgeVariant: BadgeVariant } {
  return enabled
    ? { label: 'Enabled', badgeVariant: 'secondary' }
    : { label: 'Off', badgeVariant: 'outline' };
}

/** COP-160 P82 — a run its owner cancelled: `failed` with the reason `cancelled`. */
export function isCancelledRun(run: Pick<ScheduleRunSummaryDto, 'status' | 'lastErrorClass'>): boolean {
  return run.status === 'failed' && run.lastErrorClass === 'cancelled';
}

const CANCELLABLE_RUN_STATUSES: ReadonlySet<ScheduleRunStatus> = new Set(['waiting', 'waiting_approval', 'paused']);

/** COP-160 P82 — a run that waits or is paused, with an execution to cancel. */
export function canCancelRun(run: Pick<ScheduleRunSummaryDto, 'status' | 'executionId'>): boolean {
  return CANCELLABLE_RUN_STATUSES.has(run.status) && run.executionId !== undefined;
}

export { truncateId };

export function parseToolAllowlist(text: string): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const rawPart of text.split(/[\n,]/u)) {
    const name = rawPart.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    names.push(name);
  }
  return names;
}

export function validateScheduleForm(values: ScheduleFormValues): ScheduleValidationHints {
  const hints: ScheduleValidationHints = {};
  const scheduleExpr = values.scheduleExpr.trim();
  const timezone = values.timezone.trim();
  const prompt = values.prompt.trim();
  const toolAllowlist = parseToolAllowlist(values.toolAllowlistText);

  if (!values.trackId.trim()) hints.trackId = 'Choose a track before saving.';
  if (!FIVE_FIELD_CRON_RE.test(scheduleExpr)) {
    hints.scheduleExpr = 'Use a five-field cron expression: minute hour day-of-month month day-of-week.';
  }
  if (!IANA_TIMEZONE_RE.test(timezone)) {
    hints.timezone = 'Use an IANA timezone such as America/New_York.';
  }
  if (!prompt) hints.prompt = 'Prompt is required.';
  if (toolAllowlist.length === 0) {
    hints.toolAllowlistText = 'Add at least one allowed tool name.';
  } else if (toolAllowlist.some((name) => !TOOL_NAME_RE.test(name))) {
    hints.toolAllowlistText = 'Tool names may contain letters, numbers, underscore, dot, colon, or dash only.';
  }
  if (values.wholePlanPreapproved) {
    hints.wholePlanPreapproved =
      'Whole-plan preapproval should be paired with a narrow prompt and minimal tool allowlist.';
  }

  return hints;
}

export function buildScheduleCreateBody(values: ScheduleFormValues): ScheduleCreateBody {
  return {
    trackId: values.trackId.trim(),
    scheduleExpr: values.scheduleExpr.trim(),
    timezone: values.timezone.trim(),
    prompt: values.prompt.trim(),
    toolAllowlist: parseToolAllowlist(values.toolAllowlistText),
    wholePlanPreapproved: values.wholePlanPreapproved,
    enabled: values.enabled,
    notifyOnSuccess: values.notifyOnSuccess,
  };
}

export function createScheduleFormValues({
  trackId = '',
  timezone = 'America/New_York',
}: Partial<Pick<ScheduleFormValues, 'trackId' | 'timezone'>> = {}): ScheduleFormValues {
  return {
    trackId,
    scheduleExpr: '0 9 * * 1',
    timezone,
    prompt: '',
    toolAllowlistText: '',
    wholePlanPreapproved: false,
    enabled: true,
    notifyOnSuccess: false,
  };
}

export function scheduleToFormValues(schedule: ScheduleOwnerDto): ScheduleFormValues {
  return {
    trackId: schedule.trackId,
    scheduleExpr: schedule.scheduleExpr,
    timezone: schedule.timezone,
    prompt: schedule.prompt,
    toolAllowlistText: schedule.toolAllowlist.join('\n'),
    wholePlanPreapproved: schedule.wholePlanPreapproved,
    enabled: schedule.enabled,
    notifyOnSuccess: schedule.notifyOnSuccess,
  };
}

export function buildSchedulePatchBody(
  original: ScheduleOwnerDto,
  values: ScheduleFormValues,
): SchedulePatchBody {
  const patch: SchedulePatchBody = { expectedUpdatedAt: original.updatedAt };
  const scheduleExpr = values.scheduleExpr.trim();
  const timezone = values.timezone.trim();
  const prompt = values.prompt.trim();
  const toolAllowlist = parseToolAllowlist(values.toolAllowlistText);

  if (scheduleExpr !== original.scheduleExpr) patch.scheduleExpr = scheduleExpr;
  if (timezone !== original.timezone) patch.timezone = timezone;
  if (prompt !== original.prompt) patch.prompt = prompt;
  if (!sameStringArray(toolAllowlist, original.toolAllowlist)) patch.toolAllowlist = toolAllowlist;
  if (values.wholePlanPreapproved !== original.wholePlanPreapproved) {
    patch.wholePlanPreapproved = values.wholePlanPreapproved;
  }
  if (values.enabled !== original.enabled) patch.enabled = values.enabled;
  if (values.notifyOnSuccess !== original.notifyOnSuccess) patch.notifyOnSuccess = values.notifyOnSuccess;

  return patch;
}

export function getWholePlanPreapprovalWarning(enabled: boolean): string | null {
  return enabled ? WHOLE_PLAN_PREAPPROVAL_WARNING : null;
}

export function buildScheduleListQuery(filters: ScheduleListFilters): ScheduleListQuery {
  const query: ScheduleListQuery = { limit: 100 };
  if (filters.trackId) query.trackId = filters.trackId;
  if (filters.enabledFilter === 'enabled') query.enabled = true;
  if (filters.enabledFilter === 'paused') query.enabled = false;
  return query;
}

export function buildScheduleRunsQuery(): ScheduleRunsQuery {
  return { limit: 20 };
}

export function summarizeScheduleNotifications(
  notifications: readonly NotificationOwnerDto[],
): ScheduleNotificationSummary {
  const unread = notifications.filter((notification) => notification.readAt === null);
  const highestSeverity = unread.reduce<NotificationSeverity | null>((current, notification) => {
    if (!current) return notification.severity;
    return NOTIFICATION_SEVERITY_RANK[notification.severity] > NOTIFICATION_SEVERITY_RANK[current]
      ? notification.severity
      : current;
  }, null);

  return {
    unreadCount: unread.length,
    latestLabel: notifications[0]
      ? `${notifications[0].title} · ${formatScheduleDateTime(notifications[0].createdAt)}`
      : 'No recent scheduler notifications',
    highestSeverity,
    badgeVariant: notificationSeverityBadgeVariant(highestSeverity),
  };
}

export function notificationSeverityBadgeVariant(severity: NotificationSeverity | null): BadgeVariant {
  if (severity === 'error') return 'destructive';
  if (severity === 'warning') return 'default';
  if (severity === 'info') return 'secondary';
  return 'outline';
}

export function safeScheduleNotificationsForSchedule(
  notifications: readonly NotificationOwnerDto[],
  scheduleId: string,
): NotificationOwnerDto[] {
  return notifications.filter((notification) => notification.scheduleId === scheduleId);
}

export function safeRunSummary(run: ScheduleRunSummaryDto): SafeScheduleRunSummary {
  const {
    id,
    scheduleId,
    ownerUserId,
    trackId,
    scheduledFor,
    startedAt,
    finishedAt,
    status,
    attemptCount,
    nextRetryAt,
    lastErrorClass,
    lastErrorMessage,
    traceId,
    approvalId,
    createdAt,
    updatedAt,
  } = run;

  return {
    id,
    scheduleId,
    ownerUserId,
    trackId,
    scheduledFor,
    startedAt,
    finishedAt,
    status,
    attemptCount,
    nextRetryAt,
    lastErrorClass,
    lastErrorMessage,
    traceId,
    approvalId,
    createdAt,
    updatedAt,
    statusLabel: isCancelledRun(run) ? 'Cancelled' : STATUS_LABELS[status],
    badgeVariant: isCancelledRun(run) ? 'outline' : runStatusBadgeVariant(status),
    scheduledForLabel: formatScheduleDateTime(scheduledFor),
    startedAtLabel: formatScheduleDateTime(startedAt),
    finishedAtLabel: formatScheduleDateTime(finishedAt),
    durationLabel: formatRunDuration({ startedAt, finishedAt }),
    retryLabel: nextRetryAt ? `Retry at ${formatScheduleDateTime(nextRetryAt)}` : 'No retry scheduled',
    errorLabel: formatRunError(lastErrorClass, lastErrorMessage),
    traceLabel: traceId ?? '—',
    approvalLabel: truncateId(approvalId),
    // COP-131: absent means the turn never opened a session — `—`, not a
    // rendered `null`. The key itself stays absent so the summary keeps the
    // DTO's present-or-absent contract.
    ...(run.sessionId !== undefined ? { sessionId: run.sessionId } : {}),
    sessionLabel: truncateId(run.sessionId),
  };
}

export function mapScheduleApiError(error: unknown): string {
  const status = statusFromError(error);
  const message = messageFromError(error);
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('version_conflict') || lowerMessage.includes('updated_at mismatch')) {
    return 'This schedule changed since you opened it. Refresh and try again.';
  }
  if (lowerMessage.includes('idempotency_conflict') || lowerMessage.includes('idempotency key')) {
    return 'This save was already attempted with different values. Reopen the form and try again.';
  }
  if (status === 401) return 'You need to sign in again before managing schedules.';
  if (status === 403 || lowerMessage.includes('unauthorized_track_access')) {
    return 'You are not allowed to perform that schedule action.';
  }
  if (status === 404 || lowerMessage.includes('unknown_schedule')) {
    return 'This schedule is no longer available. Refresh the list.';
  }
  if (lowerMessage.includes('six_field_cron_not_supported')) {
    return 'Use a five-field cron expression; seconds are not supported.';
  }
  if (lowerMessage.includes('cron_must_have_five_fields') || lowerMessage.includes('invalid_token')) {
    return 'Use a valid five-field cron expression.';
  }
  if (lowerMessage.includes('invalid_timezone')) {
    return 'Use a valid IANA timezone such as America/New_York.';
  }
  if (lowerMessage.includes('cadence_below_15_minutes')) {
    return 'Schedules must run at least 15 minutes apart.';
  }
  if (lowerMessage.includes('allowlist_mismatch') || lowerMessage.includes('allowlist contains unknown tools')) {
    return 'The tool allowlist contains tools this track does not expose.';
  }
  if (lowerMessage.includes('schedule_persist_failed') || lowerMessage.includes('schedule persistence failed')) {
    return 'Schedule operation could not be persisted. Try again.';
  }
  if (status === 409) {
    return 'This schedule changed since you opened it. Refresh and try again.';
  }
  if (status === 400 || status === 422 || lowerMessage.includes('invalid_schedule')) {
    return 'The schedule could not be saved. Check the highlighted fields and try again.';
  }
  return message || 'The schedule request failed. Try again.';
}

function formatRunError(errorClass: string | null, errorMessage: string | null): string {
  if (errorClass && errorMessage) return `${errorClass}: ${errorMessage}`;
  if (errorMessage) return errorMessage;
  if (errorClass) return errorClass;
  return '—';
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function sameStringArray(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function statusFromError(error: unknown): number | undefined {
  if (isRecord(error)) {
    const status = error.status ?? error.statusCode;
    if (typeof status === 'number') return status;
  }
  const message = messageFromError(error);
  const match = /failed:\s+(\d{3})\b/i.exec(message) ?? /\b(\d{3})\s+[A-Za-z]/.exec(message);
  return match ? Number(match[1]) : undefined;
}

function messageFromError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (isRecord(error) && typeof error.message === 'string' && error.message) return error.message;
  return '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
